// Custom file-upload UI for Gravity Forms file fields.
//
// GF's native <input type="file"> shows untranslatable browser chrome
// ("Choose File" / "No file chosen" — shadow-DOM, unreachable via gettext).
// The PHP filter lb_gf_fileupload_custom_ui injects translated strings as
// data-aw-choose / data-aw-empty on the wrapper; we read them here, hide the
// native input visually (keeping it functional for GF submit/validation), and
// render a branded trigger button + filename display instead.
//
// Two contexts:
// - default (e.g. contact drawer): pill trigger ("Choose file") + plain
//   filename span ("No file chosen" when empty).
// - .aw-job-form (career single): paperclip + underlined link carrying the
//   FIELD LABEL text ("Upload your CV"); chosen file renders as a chip with a
//   red × remove button. The caps .gfield_label is sr-only'd in forms.scss —
//   the trigger is the visible label.
//
// GF multi-file fields (.gform_fileupload_multifile) are owned by plupload —
// we never touch their input; in .aw-job-form we only relabel the browse
// button with the field label so it reads like the single-upload link.
//
// Re-init is idempotent (WeakSet guard). Works across GF AJAX re-renders by
// re-running on gform_post_render (jQuery) and observing new DOM nodes via
// MutationObserver. Also re-attaches on htmx:historyRestore (back/forward).
//
// initFileUpload() is NOT idempotent on its own — it injects a row/trigger/chip
// UI into the container. Boosted nav can hand the same container node back to
// a structurally-identical destination form (idiomorph preserves DOM identity
// across matching sections), so htmx:before-swap tears down the injected UI
// and drops the container from wiredContainers before the swap lands.

// In-memory guard: tracks containers wired since their last teardown. WeakSet
// survives history-restore correctly — restored DOM nodes are new objects so
// they pass the has() check even if cached HTML carried a stale data-attribute
// flag.
const wiredContainers = new WeakSet<HTMLElement>();

// [container, teardown] pairs for containers currently wired — torn down on
// htmx:before-swap (same lifecycle as embla/journey).
const teardowns: Array<[HTMLElement, () => void]> = [];

const PAPERCLIP_SVG =
  '<svg viewBox="0 0 20 11" width="20" height="11" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M0 5.5C0 2.5 2.5 0 5.5 0H16C18.2 0 20 1.8 20 4C20 6.2 18.2 8 16 8H7.5C6.1 8 5 6.9 5 5.5C5 4.1 6.1 3 7.5 3H15V5H7.4C6.8 5 6.8 6 7.4 6H16C17.1 6 18 5.1 18 4C18 2.9 17.1 2 16 2H5.5C3.6 2 2 3.6 2 5.5C2 7.4 3.6 9 5.5 9H15V11H5.5C2.5 11 0 8.5 0 5.5Z" fill="currentColor"/></svg>';

const REMOVE_SVG =
  '<svg viewBox="0 0 12 12" width="10" height="10" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M11 1L1 11M1 1l10 10" stroke="currentColor" stroke-width="2"/></svg>';

// Field label text (minus the required-indicator span) for the .gfield
// wrapping this upload container — used as the visible trigger text in the
// job form ("Upload your CV" per design).
function fieldLabelText(container: HTMLElement): string {
  const label = container.closest(".gfield")?.querySelector(".gfield_label");
  if (!label) return "";
  const clone = label.cloneNode(true) as HTMLElement;
  clone.querySelectorAll(".gfield_required").forEach((el) => el.remove());
  return clone.textContent?.trim() ?? "";
}

function initFileUpload(container: HTMLElement): void {
  // Idempotency guard — skip if already initialised.
  if (wiredContainers.has(container)) return;
  wiredContainers.add(container);

  const jobForm = container.closest(".aw-job-form") !== null;

  // Multi-file variant: plupload owns the markup (drop area + browse button +
  // preview list); styling lives in forms.scss. Only relabel the browse button
  // in the job form so it reads like the single-upload link.
  const multi = container.querySelector<HTMLElement>(".gform_fileupload_multifile");
  if (multi) {
    if (jobForm) {
      const browse = multi.querySelector<HTMLInputElement>('input[type="button"]');
      const labelText = fieldLabelText(container);
      if (browse && labelText) browse.value = labelText;
    }
    // Nothing injected — still register so wiredContainers is dropped on
    // teardown, letting a reused container re-run this branch after a boost nav.
    teardowns.push([container, () => {}]);
    return;
  }

  const choose = container.dataset.awChoose ?? "Choose file";
  const empty  = container.dataset.awEmpty  ?? "No file chosen";

  const input = container.querySelector<HTMLInputElement>('input[type="file"]');
  if (!input) {
    teardowns.push([container, () => {}]);
    return;
  }

  // Visually hide the native input while keeping it accessible + clickable-by-proxy.
  // opacity:0 + absolute sizing ensures GF can still trigger validation on it and
  // programmatic .click() from the trigger still works. pointer-events:none prevents
  // accidental direct clicks that would double-open the picker in some browsers.
  input.classList.add("aw-file-native");

  // Build custom UI.
  const trigger = document.createElement("button");
  trigger.type = "button";
  trigger.className = "aw-file-trigger";

  // Wrap trigger (+ filename UI) in a flex row, inserted before the native
  // input so the native input can stay in DOM flow at the end (required for
  // some GF versions).
  const row = document.createElement("div");
  row.className = "aw-file-row";
  row.setAttribute("aria-live", "polite");
  row.appendChild(trigger);
  container.insertBefore(row, input);

  trigger.addEventListener("click", () => input.click());

  if (jobForm) {
    // Paperclip + underlined field-label link; chosen file shows as a chip
    // with a × remove button (design: 02-job-detail application form).
    trigger.innerHTML = PAPERCLIP_SVG;
    const triggerText = document.createElement("span");
    triggerText.textContent = fieldLabelText(container) || choose;
    trigger.appendChild(triggerText);

    const chip = document.createElement("span");
    chip.className = "aw-file-chip";
    chip.hidden = true;

    const chipName = document.createElement("span");
    chipName.className = "aw-file-chip__name";

    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "aw-file-chip__remove";
    remove.setAttribute("aria-label", "Remove file");
    remove.innerHTML = REMOVE_SVG;

    chip.append(chipName, remove);
    row.appendChild(chip);

    remove.addEventListener("click", () => {
      input.value = "";
      input.dispatchEvent(new Event("change", { bubbles: true }));
      trigger.focus();
    });

    const onChange = (): void => {
      const files = input.files;
      if (!files || files.length === 0) {
        chip.hidden = true;
        chipName.textContent = "";
        return;
      }
      chipName.textContent = Array.from(files)
        .map((f) => f.name)
        .join(", ");
      chip.hidden = false;
    };
    input.addEventListener("change", onChange);

    // Teardown removes the injected row (trigger + chip) and the native
    // input's change listener, and un-hides the native input — restoring the
    // container to its pre-init state so a reused node (idiomorph) re-inits
    // cleanly instead of growing a duplicate row.
    teardowns.push([
      container,
      () => {
        input.removeEventListener("change", onChange);
        input.classList.remove("aw-file-native");
        row.remove();
      },
    ]);

    return;
  }

  // Default context: pill trigger + plain filename span.
  trigger.textContent = choose;

  const nameSpan = document.createElement("span");
  nameSpan.className = "aw-file-name";
  nameSpan.textContent = empty;
  row.appendChild(nameSpan);

  const onChange = (): void => {
    const files = input.files;
    if (!files || files.length === 0) {
      nameSpan.textContent = empty;
      return;
    }
    nameSpan.textContent = Array.from(files)
      .map((f) => f.name)
      .join(", ");
  };
  input.addEventListener("change", onChange);

  teardowns.push([
    container,
    () => {
      input.removeEventListener("change", onChange);
      input.classList.remove("aw-file-native");
      row.remove();
    },
  ]);
}

function initAll(): void {
  document
    .querySelectorAll<HTMLElement>("[data-aw-fileupload]")
    .forEach(initFileUpload);
}

// Tear down injected UI + listeners before htmx morphs the DOM. idiomorph can
// hand the same container node to a structurally-identical destination form
// (e.g. career single ↔ career single) — without this the reused container
// stays in wiredContainers forever and never re-initialises, or (if it did
// re-init) would grow a second row/trigger/chip alongside the first.
function destroyAll(): void {
  teardowns.splice(0).forEach(([container, fn]) => {
    fn();
    wiredContainers.delete(container);
  });
}

// Initial pass on DOMContentLoaded (or immediately if already ready).
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initAll);
} else {
  initAll();
}

// Re-init after htmx boost swaps or back/forward history restore.
// htmx:historyRestore fires on back/forward — htmx:load is NOT fired then.
document.addEventListener("htmx:load", initAll);
document.addEventListener("htmx:historyRestore", initAll);
document.addEventListener("htmx:before-swap", destroyAll);

// Re-init after GF AJAX re-renders the form DOM.
// gform_post_render fires on jQuery(document) after every AJAX page turn / submit.
if (typeof window !== "undefined" && (window as any).jQuery) {
  (window as any).jQuery(document).on("gform_post_render", initAll);
}

// Belt-and-suspenders: MutationObserver catches any other dynamic insertions
// (e.g. conditional logic showing a hidden file field).
const observer = new MutationObserver(() => {
  const pending = document.querySelectorAll<HTMLElement>("[data-aw-fileupload]");
  if (pending.length) initAll();
});

observer.observe(document.body, { childList: true, subtree: true });
